// ============================================================
// Summit League v2  - Leaderboard Page
// Full leaderboard with server-side sorting, pagination,
// and URL sync. Route: /leaderboard (game subdomain only)
// ============================================================

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useGame } from '../hooks/useGame';
import { useToast } from '../hooks/useToast';
import axiosClient from '../api/axiosClient';

// Components
import SeasonHeader from '../components/dashboard/SeasonHeader';
import { getRankClass, DefaultTeamIcon } from '../components/dashboard/DashboardHelpers';
import Pagination from '../components/ui/Pagination';
import type { PaginationInfo } from '../components/ui/Pagination';
import ToastContainer from '../components/ui/Toast';

// Styles
import '../styles/leaderboard.css';

// ---- Types ----

type LeaderboardSortField = 'rank' | 'points' | 'wins' | 'losses' | 'name';
type SortOrder = 'asc' | 'desc';

interface LeaderboardTeamEntry {
  rank: number | null;
  teamId: number;
  teamName: string;
  teamTag: string | null;
  logoUrl: string | null;
  points: number;
  wins: number;
  losses: number;
  matchesPlayed: number;
  winRate: number;
  teamUrl: string;
}

interface LeaderboardApiResponse {
  success: boolean;
  data: {
    teams: LeaderboardTeamEntry[];
    pagination: PaginationInfo;
    sort: {
      field: LeaderboardSortField;
      order: SortOrder;
    };
  };
}

// ---- Constants ----

const DEFAULT_LIMIT = 50;
const VALID_SORT_FIELDS: LeaderboardSortField[] = ['rank', 'points', 'wins', 'losses', 'name'];
const VALID_ORDERS: SortOrder[] = ['asc', 'desc'];
const SKELETON_ROWS = 12;

// ---- Helpers ----

function formatPoints(pts: number): string {
  if (Number.isInteger(pts)) return String(pts);
  // Up to 2 decimals, trimming trailing zeros
  return parseFloat(pts.toFixed(2)).toString();
}

function parseSortField(raw: string | null): LeaderboardSortField {
  if (raw && VALID_SORT_FIELDS.includes(raw as LeaderboardSortField)) {
    return raw as LeaderboardSortField;
  }
  return 'rank';
}

function parseSortOrder(raw: string | null): SortOrder {
  if (raw && VALID_ORDERS.includes(raw as SortOrder)) {
    return raw as SortOrder;
  }
  return 'asc';
}

function parsePage(raw: string | null): number {
  const n = parseInt(raw || '', 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

// ---- Component ----

const LeaderboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { gameId, hasGame, season } = useGame();
  const toast = useToast();

  // Ref to avoid toast in useCallback deps (prevents infinite re-render loop)
  const toastRef = useRef(toast);
  toastRef.current = toast;

  // Parse URL params
  const sortField = parseSortField(searchParams.get('sort'));
  const sortOrder = parseSortOrder(searchParams.get('order'));
  const page = parsePage(searchParams.get('page'));

  // State
  const [teams, setTeams] = useState<LeaderboardTeamEntry[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ---- Fetch leaderboard ----

  const fetchLeaderboard = useCallback(async () => {
    if (!gameId) return;

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        game_id: String(gameId),
        page: String(page),
        limit: String(DEFAULT_LIMIT),
        sort: sortField,
        order: sortOrder,
      });

      const res = await axiosClient.get<LeaderboardApiResponse>(
        `/leaderboard?${params.toString()}`
      );

      const data = res.data?.data ?? res.data;
      setTeams(data.teams ?? []);
      setPagination(data.pagination ?? null);
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        'Failed to load leaderboard.';
      setError(message);
      toastRef.current.error('Error', message);
    } finally {
      setLoading(false);
    }
  }, [gameId, page, sortField, sortOrder]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  // ---- URL sync helpers ----

  const updateParams = useCallback(
    (updates: Record<string, string>) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([k, v]) => {
        // Remove defaults to keep URL clean
        if (
          (k === 'sort' && v === 'rank') ||
          (k === 'order' && v === 'asc') ||
          (k === 'page' && v === '1')
        ) {
          next.delete(k);
        } else {
          next.set(k, v);
        }
      });
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleSort = useCallback(
    (field: LeaderboardSortField) => {
      let newOrder: SortOrder;
      if (sortField === field) {
        // Toggle direction
        newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        // New column: rank defaults to asc, everything else desc
        newOrder = field === 'rank' ? 'asc' : 'desc';
      }
      updateParams({ sort: field, order: newOrder, page: '1' });
    },
    [sortField, sortOrder, updateParams]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      updateParams({ page: String(newPage) });
      // Scroll to top of table
      document.querySelector('.sl-lb-card')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    },
    [updateParams]
  );

  const handleTeamClick = useCallback(
    (teamUrl: string) => {
      navigate(teamUrl);
    },
    [navigate]
  );

  const handleRetry = useCallback(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  // ---- Sort indicator ----

  const renderSortArrow = (field: LeaderboardSortField) => {
    if (sortField !== field) return null;
    return (
      <span className="sl-lb-sort-arrow">
        {sortOrder === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  const headerClass = (field: LeaderboardSortField) =>
    `sl-lb-th sl-lb-th--sortable${sortField === field ? ' sl-lb-th--active' : ''}`;

  // ---- No game context ----

  if (!hasGame) {
    return (
      <div className="sl-lb-page">
        <div className="sl-lb-page-inner">
          <div className="sl-card">
            <h2 className="sl-section-title">No Game Selected</h2>
            <p className="sl-card-subtitle">
              Visit a game-specific subdomain to view the leaderboard.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ---- Render ----

  return (
    <div className="sl-lb-page">
      <div className="sl-lb-page-inner">
        {/* Season Header Hero */}
        <SeasonHeader season={season} loading={false} />

        {/* Error Banner */}
        {error && !loading && (
          <div className="sl-dashboard-error">
            <span>{error}</span>
            <button className="sl-dashboard-error-retry" onClick={handleRetry}>
              Retry
            </button>
          </div>
        )}

        {/* Leaderboard Card */}
        <section className="sl-card sl-lb-card">
          <div className="sl-card-header sl-lb-header">
            <h2 className="sl-section-title">Leaderboard</h2>
            {pagination && !loading && (
              <span className="sl-lb-header-count">
                {pagination.total} {pagination.total === 1 ? 'team' : 'teams'}
              </span>
            )}
          </div>

          {/* Loading Skeleton */}
          {loading ? (
            <div className="sl-lb-skeleton">
              {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <div key={i} className="sl-skeleton-row sl-skeleton-pulse" />
              ))}
            </div>
          ) : teams.length === 0 ? (
            /* Empty State */
            <div className="sl-lb-empty">
              <p className="sl-lb-empty-title">No teams ranked yet</p>
              <p className="sl-lb-empty-text">
                Once matches are played and reported, teams will appear on the
                leaderboard.
              </p>
            </div>
          ) : (
            /* Table */
            <>
              <div className="sl-lb-table-wrap">
                <div className="sl-lb-table">
                  {/* Header Row */}
                  <div className="sl-lb-thead">
                    <span className="sl-lb-th sl-lb-th--rank">#</span>
                    <span className="sl-lb-th sl-lb-th--team">Team</span>
                    <button
                      type="button"
                      className={headerClass('points')}
                      onClick={() => handleSort('points')}
                    >
                      PTS{renderSortArrow('points')}
                    </button>
                    <button
                      type="button"
                      className={headerClass('wins')}
                      onClick={() => handleSort('wins')}
                    >
                      W{renderSortArrow('wins')}
                    </button>
                    <button
                      type="button"
                      className={headerClass('losses')}
                      onClick={() => handleSort('losses')}
                    >
                      L{renderSortArrow('losses')}
                    </button>

                  </div>

                  {/* Data Rows */}
                  {teams.map((team) => (
                    <button
                      key={team.teamId}
                      type="button"
                      className="sl-lb-row"
                      onClick={() => handleTeamClick(team.teamUrl)}
                    >
                      <span
                        className={`sl-lb-rank ${getRankClass(team.rank)}`}
                      >
                        {team.rank ?? '—'}
                      </span>

                      <div className="sl-lb-team">
                        <div className="sl-lb-team-logo-wrap">
                          {team.logoUrl ? (
                            <img
                              src={team.logoUrl}
                              alt={`${team.teamName} logo`}
                              className="sl-lb-team-logo"
                            />
                          ) : (
                            <DefaultTeamIcon />
                          )}
                        </div>
                        <div className="sl-lb-team-info">
                          <span className="sl-lb-team-name">
                            {team.teamName}
                          </span>
                          {team.teamTag && (
                            <span className="sl-lb-team-tag">
                              [{team.teamTag}]
                            </span>
                          )}
                        </div>
                      </div>

                      <span className="sl-lb-stat sl-lb-stat--pts">
                        {formatPoints(team.points)}
                      </span>
                      <span className="sl-lb-stat">{team.wins}</span>
                      <span className="sl-lb-stat">{team.losses}</span>

                    </button>
                  ))}
                </div>
              </div>

              {/* Pagination */}
              {pagination && (
                <Pagination
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  loading={loading}
                  itemLabel="teams"
                />
              )}
            </>
          )}

          {/* Back to Dashboard */}
          <div className="sl-lb-footer">
            <button
              type="button"
              className="sl-link-button"
              onClick={() => navigate('/')}
            >
              &larr; Back to Dashboard
            </button>
          </div>
        </section>
      </div>

      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />
    </div>
  );
};

export default LeaderboardPage;